import { Link, useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { Track } from "@shared/schema";
import { TrackCard } from "@/components/track-card";
import { Search, FileText, Music, ShieldCheck, Zap, Crown } from "lucide-react";
import { useCallback } from "react";
import { motion } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  visible: { transition: { staggerChildren: 0.08 } },
};

export default function Home() {
  const { data, isLoading } = useQuery<{
    tracks: Track[];
    pagination: { total: number; page: number; limit: number; totalPages: number };
  }>({
    queryKey: ["/api/tracks", { limit: 8 }],
  });

  const tracks = data?.tracks;
  const [, navigate] = useLocation();

  const handleStartLicensing = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => navigate("/license"), 500);
  }, [navigate]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#180E26", color: "#fff" }}>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(circle at 15% 20%, rgba(242,110,34,0.18), transparent 55%), radial-gradient(circle at 85% 75%, rgba(94,191,164,0.12), transparent 55%), #180E26",
          }}
        />
        <div
          className="absolute inset-0 -z-10 opacity-[0.05] mix-blend-overlay pointer-events-none"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />

        <div className="container mx-auto px-6 py-24 md:py-36 relative">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.div
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-6 text-sm"
              style={{
                background: "rgba(94,191,164,0.10)",
                border: "1px solid rgba(94,191,164,0.35)",
                color: "#9ed5c4",
              }}
            >
              <ShieldCheck className="w-3.5 h-3.5" />
              100% owned masters + publishing. No labels. No PRO carveouts.
            </motion.div>
            <motion.h1
              variants={fadeUp}
              className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 tracking-tight"
            >
              License original music
              <br />
              <span style={{ color: "#F26E22" }}>direct from the artist.</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lg md:text-xl mb-10 max-w-2xl"
              style={{ color: "#b0b0b0" }}
            >
              882 tracks. Zero sync-agency middlemen. Clean cap table on every cue. Built for the
              filmmakers, agencies, and creators who actually want to call the artist.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
              <Link href="/catalog">
                <Button
                  size="lg"
                  className="rounded-full px-8 font-semibold"
                  style={{ background: "#F26E22", color: "#180E26" }}
                >
                  Browse the catalog
                </Button>
              </Link>
              <Button
                size="lg"
                variant="outline"
                onClick={handleStartLicensing}
                className="rounded-full px-8 font-semibold"
                style={{
                  background: "transparent",
                  border: "1.5px solid rgba(255,255,255,0.25)",
                  color: "#fff",
                }}
              >
                Start a license request
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto"
          >
            {[
              { value: "882", label: "Original tracks" },
              { value: "100%", label: "Owned masters" },
              { value: "0%", label: "Label or PRO cuts" },
              { value: "1 reply", label: "Direct to the artist" },
            ].map((s) => (
              <motion.div
                key={s.label}
                variants={fadeUp}
                className="text-center rounded-2xl px-4 py-8"
                style={{
                  background: "rgba(30,20,50,0.6)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <p className="text-4xl md:text-5xl font-bold mb-2" style={{ color: "#F26E22" }}>
                  {s.value}
                </p>
                <p className="text-sm" style={{ color: "#b0b0b0" }}>
                  {s.label}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Featured tracks */}
      <section id="featured" className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            className="mb-12 max-w-3xl"
          >
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold mb-4">
              Listen first. Decide second.
            </motion.h2>
            <motion.p variants={fadeUp} style={{ color: "#b0b0b0" }}>
              The whole catalog is here, streamable, no sign-up required. Find something that fits
              your project, then send a license request.
            </motion.p>
          </motion.div>

          {isLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-2xl overflow-hidden h-[360px] animate-pulse"
                  style={{ background: "rgba(30,20,50,0.5)" }}
                />
              ))}
            </div>
          ) : tracks && tracks.length > 0 ? (
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={stagger}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
            >
              {tracks.slice(0, 4).map((track) => (
                <motion.div key={track.id} variants={fadeUp}>
                  <TrackCard track={track} />
                </motion.div>
              ))}
            </motion.div>
          ) : (
            <div
              className="text-center py-16 rounded-2xl"
              style={{
                background: "rgba(30,20,50,0.4)",
                border: "1px dashed rgba(255,255,255,0.08)",
                color: "#b0b0b0",
              }}
            >
              Catalog seeding in progress. 882 tracks landing shortly.
            </div>
          )}

          <div className="text-center mt-12">
            <Link href="/catalog">
              <Button
                variant="outline"
                className="rounded-full px-8"
                style={{
                  background: "transparent",
                  border: "1.5px solid rgba(242,110,34,0.6)",
                  color: "#F26E22",
                }}
              >
                Browse all 882 tracks
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            className="mb-14 text-center max-w-3xl mx-auto"
          >
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold mb-4">
              Three steps. No phone calls. No NDAs.
            </motion.h2>
            <motion.p variants={fadeUp} style={{ color: "#b0b0b0" }}>
              The old way: email a sync agency, wait three weeks, get quoted $8k for a track they
              don't even own. This way: pick a track, sign a license, ship.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto"
          >
            {[
              {
                icon: Search,
                title: "1. Browse",
                body:
                  "Filter by genre, mood, BPM, or use case. Stream the full track, not a 15-second snippet.",
              },
              {
                icon: FileText,
                title: "2. Request",
                body:
                  "Tell us how you'll use it. AI checks the fit, the artist confirms, you get a clean license PDF.",
              },
              {
                icon: Music,
                title: "3. Ship",
                body:
                  "Pay via Stripe. Download a 320kbps MP3 that's yours to use under the agreed terms.",
              },
            ].map((step) => (
              <motion.div
                key={step.title}
                variants={fadeUp}
                className="rounded-2xl p-8"
                style={{
                  background: "rgba(30,20,50,0.6)",
                  border: "1px solid rgba(255,255,255,0.06)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: "rgba(242,110,34,0.18)", color: "#F26E22" }}
                >
                  <step.icon className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold mb-2">{step.title}</h3>
                <p style={{ color: "#b0b0b0" }}>{step.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why direct */}
      <section className="py-16">
        <div className="container mx-auto px-6 max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={stagger}
            className="mb-14 max-w-3xl"
          >
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold mb-4">
              Why direct beats every sync agency on earth.
            </motion.h2>
            <motion.p variants={fadeUp} style={{ color: "#b0b0b0" }}>
              The artist is the rights holder. The artist is the email reply. The artist is the
              entire approval chain.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {[
              {
                icon: Crown,
                title: "100% ownership",
                body:
                  "Every master and every publishing share lives with the artist. No label sign-off. No PRO carveout.",
              },
              {
                icon: Zap,
                title: "Same-day turnaround",
                body:
                  "AI evaluates your use case at submission. Approved requests get a license PDF in hours, not weeks.",
              },
              {
                icon: ShieldCheck,
                title: "Clean cap table",
                body:
                  "One signature on the license. One name on the invoice. Zero retroactive claim risk down the road.",
              },
            ].map((c) => (
              <motion.div
                key={c.title}
                variants={fadeUp}
                className="rounded-2xl p-8"
                style={{
                  background: "rgba(30,20,50,0.6)",
                  border: "1px solid rgba(94,191,164,0.18)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: "rgba(94,191,164,0.16)", color: "#5EBFA4" }}
                >
                  <c.icon className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-bold mb-2">{c.title}</h3>
                <p style={{ color: "#b0b0b0" }}>{c.body}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 relative overflow-hidden">
        <div
          className="absolute inset-0 -z-10"
          style={{
            background:
              "radial-gradient(circle at 50% 50%, rgba(242,110,34,0.14), transparent 60%)",
          }}
        />
        <div className="container mx-auto px-6 text-center max-w-3xl">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            Find your track. Send a request.
            <br />
            <span style={{ color: "#F26E22" }}>License it before lunch.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg mb-10"
            style={{ color: "#b0b0b0" }}
          >
            The artist replies. The license is clean. The music is yours to use.
          </motion.p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/catalog">
              <Button
                size="lg"
                className="rounded-full px-8 font-semibold"
                style={{ background: "#F26E22", color: "#180E26" }}
              >
                Browse the catalog
              </Button>
            </Link>
            <Link href="/license">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-8 font-semibold"
                style={{
                  background: "transparent",
                  border: "1.5px solid rgba(255,255,255,0.25)",
                  color: "#fff",
                }}
              >
                Start a license request
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
